/**
 * @voltxampere/persistence — Bookmarks store (Sprint 32-4)
 *
 * Per-row bookmarks live in the `bookmarks` object store keyed by
 * `id`. A `by_circuitId` index supports the per-preset load path
 * (`listBookmarksForCircuit(circuitId)`) without a full scan; a
 * cursor-based `listAllBookmarks()` exists so the boot path can
 * hydrate the in-memory store wholesale.
 *
 * The persistence layer is invariant-free: it stores whatever it's
 * given. Bounds (note ≤ 280 chars, t ≥ 0 finite) are enforced in
 * `apps/web/src/store/bookmarks.ts` so the rules live next to the
 * UI that surfaces them.
 *
 * Public API:
 *
 *  - listAllBookmarks()                        — boot hydrate
 *  - listBookmarksForCircuit(circuitId)        — per-preset hydrate
 *  - putBookmark(b)                            — upsert one row
 *  - deleteBookmark(id)                        — delete by id
 *  - clearBookmarks()                          — wipe everything
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { openDb, requestToPromise, transactionDone } from "./db.js";
import { INDEX_BOOKMARKS_CIRCUIT_ID, STORE_BOOKMARKS } from "./types.js";
/**
 * Read every bookmark across every circuit. Boot path uses this to
 * populate the all-circuits in-memory ring; the UI then filters
 * per-active-circuit.
 *
 * Returns `[]` when the store is empty (fresh install).
 */
export async function listAllBookmarks() {
    const db = await openDb();
    const tx = db.transaction(STORE_BOOKMARKS, "readonly");
    const store = tx.objectStore(STORE_BOOKMARKS);
    const raw = await requestToPromise(store.getAll());
    await transactionDone(tx);
    if (!Array.isArray(raw))
        return [];
    return raw.filter(isStoredBookmark);
}
/**
 * Read every bookmark whose `circuitId` matches the given id. Uses
 * the `by_circuitId` IDB index so the lookup is O(log n) rather than
 * a full-store scan. Returns `[]` when no rows match.
 */
export async function listBookmarksForCircuit(circuitId) {
    const db = await openDb();
    const tx = db.transaction(STORE_BOOKMARKS, "readonly");
    const store = tx.objectStore(STORE_BOOKMARKS);
    const index = store.index(INDEX_BOOKMARKS_CIRCUIT_ID);
    const raw = await requestToPromise(index.getAll(circuitId));
    await transactionDone(tx);
    if (!Array.isArray(raw))
        return [];
    return raw.filter(isStoredBookmark);
}
/**
 * Insert or update a bookmark row. The IDB primary key (`id`)
 * determines whether the operation is an insert or an update —
 * `put` upserts.
 */
export async function putBookmark(bookmark) {
    const db = await openDb();
    const tx = db.transaction(STORE_BOOKMARKS, "readwrite");
    const store = tx.objectStore(STORE_BOOKMARKS);
    await requestToPromise(store.put(bookmark));
    await transactionDone(tx);
}
/**
 * Delete a bookmark by id. No-op when the id doesn't exist (IDB's
 * `delete` is idempotent).
 */
export async function deleteBookmark(id) {
    const db = await openDb();
    const tx = db.transaction(STORE_BOOKMARKS, "readwrite");
    const store = tx.objectStore(STORE_BOOKMARKS);
    await requestToPromise(store.delete(id));
    await transactionDone(tx);
}
/**
 * Wipe every bookmark row. Used by AYARLAR "Tümünü temizle" alongside
 * circuits / recents / commandRecents / settings clears.
 */
export async function clearBookmarks() {
    const db = await openDb();
    const tx = db.transaction(STORE_BOOKMARKS, "readwrite");
    const store = tx.objectStore(STORE_BOOKMARKS);
    await requestToPromise(store.clear());
    await transactionDone(tx);
}
/**
 * Type guard — defensive, in case a future migration leaves a
 * shape mismatch in storage.
 */
function isStoredBookmark(value) {
    if (value === null || typeof value !== "object")
        return false;
    const v = value;
    return (typeof v.id === "string" &&
        typeof v.circuitId === "string" &&
        typeof v.t === "number" &&
        typeof v.note === "string" &&
        typeof v.createdAt === "number");
}
//# sourceMappingURL=bookmarks-store.js.map